import { Link } from "react-router-dom";
import {
  ArrowRight,
  Barbell,
  ChartLineUp,
  CheckCircle,
  Globe,
  ForkKnife,
  Sparkle,
} from "@phosphor-icons/react";
import { useLanguage } from "../context/LanguageContext";

const WORKOUT_IMAGE = "https://images.unsplash.com/photo-1689007669034-9ef988d89742?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxhdGhsZXRlJTIwd29ya291dHxlbnwwfHx8fDE3NzU2NzQyMDV8MA&ixlib=rb-4.1.0&q=85";
const NUTRITION_IMAGE = "https://images.unsplash.com/photo-1749146878408-ae151b024c8a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHxoZWFsdGh5JTIwZm9vZCUyMHRhYmxlJTIwY29sb3JmdWwlMjBzYWxhZCUyMHByb3RlaW58ZW58MHx8fHwxNzc1Njc1NDUzfDA&ixlib=rb-4.1.0&q=85";
const TRACKING_IMAGE = "https://images.unsplash.com/photo-1691845300217-7432abd31ed4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtdXNjdWxhciUyMG1hbiUyMHdvbWFuJTIwZ3ltJTIwY291cGxlJTIwd29ya291dHxlbnwwfHx8fDE3NzU2NzY4NzV8MA&ixlib=rb-4.1.0&q=85";

const ATHLETE_IMAGES = [
  {
    src: "https://static.prod-images.emergentagent.com/jobs/0244f962-b610-49f9-adb1-07a23004887d/images/68c2349a476f4fd22664f84d8fae44b949cb0fd96c229be01779f63919cae861.png",
    label: "Homme",
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/0244f962-b610-49f9-adb1-07a23004887d/images/11a194782ddad3b0d4b747ce53ba6279dd75fb0df89c56bb153b750c881d4fbf.png",
    label: "Femme",
  },
];

const featureCards = [
  {
    icon: ForkKnife,
    title: "Nutrition claire",
    body: "Suis tes repas, tes macros et tes calories sans te perdre dans des tableaux compliqués.",
    image: NUTRITION_IMAGE,
    testId: "landing-feature-nutrition",
  },
  {
    icon: Barbell,
    title: "Entraînement guidé",
    body: "Planifie tes séances, ajoute tes exercices et garde une trace nette de ce que tu fais vraiment.",
    image: "https://images.unsplash.com/photo-1667890787288-ecaaa4bc6994?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxtdXNjdWxhciUyMG1hbiUyMHRhbmslMjB0b3AlMjBzaG9ydHMlMjBneW18ZW58MHx8fHwxNzc1Njc2ODc0fDA&ixlib=rb-4.1.0&q=85",
    testId: "landing-feature-workouts",
  },
  {
    icon: ChartLineUp,
    title: "Suivi précis",
    body: "Visualise ton évolution, ton poids et tes objectifs pour rester motivé sans te disperser.",
    image: TRACKING_IMAGE,
    testId: "landing-feature-progress",
  },
];

const copy = {
  fr: {
    onboarding: "Personnaliser mon profil",
    dashboard: "Entrer dans le tableau de bord",
    buyNow: "Acheter maintenant",
    nutritionCta: "Voir mon programme nutrition",
    eyebrow: "suivi complet myfitbody.fr",
    title: "Nutrition, entraînement et progression dans un seul espace.",
    description: "Centralise tes repas, tes séances et ton évolution dans un tableau de bord clair, premium et pensé pour t'aider à rester régulier au quotidien.",
    primaryCta: "Voir mon tableau de bord",
    secondaryCta: "Créer mon programme",
    metricOne: "suivies en temps réel",
    metricTwo: "organisées simplement",
    metricThree: "lisibles d'un coup d'œil",
    heroIntro: "Retrouve tout ton suivi dans une interface simple, nette et motivante.",
    nutritionPreviewKicker: "Aperçu nutrition",
    nutritionPreviewTitle: "Un programme nutrition visible dès l'accueil.",
    nutritionPreviewBody: "Visualise tout de suite tes calories, tes macros, tes repas et la qualité de ton alimentation dans un espace lisible et motivant.",
    nutritionPreviewPoints: [
      "Objectif calories et macros clair",
      "Suivi repas simple au quotidien",
      "Lecture rapide de ton équilibre alimentaire",
    ],
  },
  en: {
    onboarding: "Personalize my profile",
    dashboard: "Open dashboard",
    buyNow: "Buy now",
    nutritionCta: "See my nutrition program",
    eyebrow: "complete myfitbody.fr system",
    title: "Nutrition, training, and progress in one place.",
    description: "Bring together your meals, your workouts, and your evolution inside a clear premium dashboard built to keep you consistent every day.",
    primaryCta: "See my dashboard",
    secondaryCta: "Build my program",
    metricOne: "tracked in real time",
    metricTwo: "organized simply",
    metricThree: "easy to read",
    heroIntro: "Find all your tracking inside a clean, motivating interface.",
    nutritionPreviewKicker: "Nutrition preview",
    nutritionPreviewTitle: "A nutrition program visible from the start.",
    nutritionPreviewBody: "See your calories, macros, meals, and nutrition quality right away inside a clear motivating space.",
    nutritionPreviewPoints: [
      "Clear calorie and macro target",
      "Simple meal tracking every day",
      "Fast reading of your food balance",
    ],
  },
};

const dashboardHighlights = [
  "Tes calories et tes macros du jour",
  "Tes séances enregistrées et tes calories brûlées",
  "Ton historique de poids et ta progression",
];

export default function LandingPage({ profile }) {
  const { language, setLanguage } = useLanguage();
  const t = copy[language];
  const firstName = profile?.name?.split(" ")[0];

  return (
    <div className="landing-shell" data-testid="landing-page">
      <section className="landing-hero-section px-4 pb-16 pt-4 md:px-8 lg:px-10 lg:pb-24 lg:pt-6">
        <div className="mx-auto max-w-7xl overflow-hidden rounded-[2rem] border border-black/8 bg-[linear-gradient(135deg,#ffffff_0%,#fff4ee_52%,#eef7ff_100%)] text-[#0A0A0A] shadow-[0_30px_80px_rgba(15,23,42,0.10)]">
          <div className="landing-topbar grid items-center gap-4 border-b border-black/6 px-5 py-4 md:px-8 lg:grid-cols-[1fr_auto_1fr]">
            <div />
            <div className="flex items-center gap-3 justify-self-center" data-testid="landing-brand-block">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FF6B35] text-lg font-black text-white">M</div>
              <div>
                <p className="font-display text-3xl leading-none text-[#0A0A0A]" data-testid="landing-brand-name">myfitbody.fr</p>
                <p className="mt-1 text-xs uppercase tracking-[0.32em] text-black/45" data-testid="landing-brand-tagline">
                  nutrition · entraînement · suivi
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-self-end">
              <div className="flex items-center gap-1 rounded-full border border-black/8 bg-white px-1 py-1" data-testid="landing-language-toggle">
                <Globe size={16} className="ml-2 text-black/45" />
                <button className={`px-3 py-1.5 rounded-full text-xs font-bold ${language === 'fr' ? 'bg-[#0A0A0A] text-white' : 'text-black/55'}`} data-testid="landing-language-fr" onClick={() => setLanguage('fr')} type="button">FR</button>
                <button className={`px-3 py-1.5 rounded-full text-xs font-bold ${language === 'en' ? 'bg-[#0A0A0A] text-white' : 'text-black/55'}`} data-testid="landing-language-en" onClick={() => setLanguage('en')} type="button">EN</button>
              </div>
              <Link
                className="landing-link-pill"
                data-testid="landing-nav-onboarding"
                to="/onboarding"
              >
                {t.onboarding}
              </Link>
              <Link className="landing-link-pill" data-testid="landing-nav-buy-now" to="/buy-now">{t.buyNow}</Link>
              <Link className="landing-link-pill" data-testid="landing-nav-nutrition" to="/nutrition">{t.nutritionCta}</Link>
              <Link
                className="landing-link-pill active"
                data-testid="landing-nav-dashboard"
                to="/dashboard"
              >
                {t.dashboard}
              </Link>
            </div>
          </div>

          <div className="grid gap-0 lg:grid-cols-[1.08fr_0.92fr]">
            <div className="px-5 py-12 md:px-8 lg:px-10 lg:py-16">
              <div className="inline-flex items-center gap-2 rounded-full border border-black/8 bg-white/80 px-4 py-2 text-xs uppercase tracking-[0.3em] text-black/65" data-testid="landing-hero-eyebrow">
                <Sparkle size={14} weight="fill" />
                {t.eyebrow}
              </div>

              <h1 className="mt-6 max-w-3xl font-display text-5xl uppercase leading-[0.92] tracking-tight text-[#0A0A0A] md:text-7xl" data-testid="landing-hero-title">
                {t.title}
              </h1>

              <p className="mt-6 max-w-2xl text-base leading-7 text-black/68 md:text-lg" data-testid="landing-hero-description">
                {t.description}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  className="landing-primary-button"
                  data-testid="landing-primary-cta"
                  to="/dashboard"
                >
                  {t.primaryCta}
                  <ArrowRight size={18} weight="bold" />
                </Link>
                <Link
                  className="landing-secondary-button"
                  data-testid="landing-secondary-cta"
                  to="/onboarding"
                >
                  {t.secondaryCta}
                </Link>
                <Link
                  className="landing-secondary-button light"
                  data-testid="landing-nutrition-cta"
                  to="/nutrition"
                >
                  {t.nutritionCta}
                </Link>
              </div>

              <div className="mt-8 grid gap-3 md:grid-cols-3">
                {[
                  { value: "Macros", label: t.metricOne },
                  { value: "Séances", label: t.metricTwo },
                  { value: "Courbes", label: t.metricThree },
                ].map((item, index) => (
                  <div className="landing-metric-box" data-testid={`landing-metric-${index}`} key={item.value}>
                    <p className="font-display text-3xl uppercase text-[#0A0A0A]">{item.value}</p>
                    <p className="mt-2 text-sm text-black/55">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative min-h-[420px] overflow-hidden p-5 lg:min-h-full" data-testid="landing-hero-visual">
              <div className="hero-collage" data-testid="landing-athlete-collage">
                {ATHLETE_IMAGES.map((image, index) => (
                  <div className="hero-collage-card" data-testid={`landing-athlete-card-${index}`} key={image.label}>
                    <img alt={image.label} className="hero-collage-image" src={image.src} />
                  </div>
                ))}
              </div>
              <div className="absolute bottom-6 left-6 right-6 rounded-[1.75rem] border border-white/15 bg-black/58 p-5 backdrop-blur-xl" data-testid="landing-hero-card">
                <p className="text-xs uppercase tracking-[0.3em] text-white/65">À l'intérieur</p>
                <h2 className="mt-3 font-display text-4xl uppercase text-white">Ton cockpit fitness</h2>
                <p className="mt-3 text-sm leading-6 text-white/72">
                  {firstName ? `Salut ${firstName}, ${t.heroIntro.toLowerCase()}` : t.heroIntro}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-10 md:px-8 lg:px-10" data-testid="landing-features-section">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 max-w-3xl">
            <p className="landing-section-kicker" data-testid="landing-features-kicker">Le trio qui te fait avancer</p>
            <h2 className="landing-section-title text-black" data-testid="landing-features-title">
              Tout ce qu'il te faut pour manger mieux, t'entraîner sérieusement et mesurer ce qui change.
            </h2>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {featureCards.map((card) => {
              const Icon = card.icon;

              return (
                <article className="feature-card" data-testid={card.testId} key={card.title}>
                  <div className="feature-card-image-wrap">
                    <img alt={card.title} className="feature-card-image" src={card.image} />
                  </div>
                  <div className="feature-card-content">
                    <div className="feature-icon-box">
                      <Icon size={20} weight="fill" />
                    </div>
                    <h3 className="font-display text-3xl uppercase text-[#0A0A0A]">{card.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-[#505050]">{card.body}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-4 py-4 md:px-8 lg:px-10" data-testid="landing-nutrition-preview-section">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-black/8 bg-[linear-gradient(135deg,#fff6f1_0%,#ffffff_48%,#f4f9ff_100%)] p-6 shadow-[0_20px_45px_rgba(15,23,42,0.06)] lg:grid lg:grid-cols-[0.92fr_1.08fr] lg:gap-6 lg:p-8">
          <div>
            <p className="landing-section-kicker" data-testid="landing-nutrition-preview-kicker">{t.nutritionPreviewKicker}</p>
            <h2 className="landing-section-title text-black" data-testid="landing-nutrition-preview-title">
              {t.nutritionPreviewTitle}
            </h2>
            <p className="mt-4 max-w-xl text-base leading-7 text-[#505050]" data-testid="landing-nutrition-preview-body">
              {t.nutritionPreviewBody}
            </p>

            <div className="mt-6 space-y-3">
              {t.nutritionPreviewPoints.map((item, index) => (
                <div className="flex items-start gap-3 rounded-2xl border border-black/6 bg-white/80 px-4 py-4" data-testid={`landing-nutrition-preview-point-${index}`} key={item}>
                  <CheckCircle size={18} weight="fill" className="mt-1 text-[#FF6B35]" />
                  <p className="text-sm leading-6 text-[#303030]">{item}</p>
                </div>
              ))}
            </div>

            <Link className="landing-primary-button mt-6" data-testid="landing-nutrition-preview-button" to="/nutrition">
              {t.nutritionCta}
            </Link>
          </div>

          <div className="nutrition-preview-grid" data-testid="landing-nutrition-preview-grid">
            <div className="nutrition-preview-card hero">
              <p className="preview-label">Calories</p>
              <p className="preview-value small">1 845 kcal</p>
              <div className="preview-progress"><span style={{ width: "76%" }} /></div>
              <p className="preview-meta">Objectif du jour : 2 420 kcal</p>
            </div>
            <div className="nutrition-preview-card image">
              <img alt="Nutrition équilibrée" className="feature-card-image" src={NUTRITION_IMAGE} />
            </div>
            <div className="nutrition-preview-card dark">
              <p className="preview-label">Macros</p>
              <p className="preview-value small">P 132 · G 180 · L 58</p>
              <p className="preview-meta">Répartition nette et simple à suivre.</p>
            </div>
            <div className="nutrition-preview-card wide">
              <p className="preview-label">Repas récents</p>
              <div className="mt-3 space-y-3 text-sm text-[#303030]">
                <div className="flex items-center justify-between rounded-2xl bg-[#F8F9FA] px-4 py-3"><span>Petit-déjeuner protéiné</span><span>420 kcal</span></div>
                <div className="flex items-center justify-between rounded-2xl bg-[#F8F9FA] px-4 py-3"><span>Déjeuner équilibré</span><span>610 kcal</span></div>
                <div className="flex items-center justify-between rounded-2xl bg-[#F8F9FA] px-4 py-3"><span>Snack contrôlé</span><span>185 kcal</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-8 md:px-8 lg:px-10" data-testid="landing-dashboard-preview-section">
        <div className="mx-auto grid max-w-7xl gap-6 rounded-[2rem] border border-black/8 bg-white p-6 shadow-[0_20px_45px_rgba(15,23,42,0.08)] lg:grid-cols-[0.9fr_1.1fr] lg:p-8">
          <div>
            <p className="landing-section-kicker" data-testid="landing-dashboard-kicker">Avant d'entrer dedans</p>
            <h2 className="landing-section-title text-black" data-testid="landing-dashboard-title">
              Un tableau de bord net, rapide et pensé pour l'action.
            </h2>
            <p className="mt-4 max-w-xl text-base leading-7 text-[#505050]" data-testid="landing-dashboard-description">
              Tu retrouves en un coup d'œil tes chiffres du jour, tes objectifs nutritionnels, tes séances et ta progression, sans écran inutile ni surcharge visuelle.
            </p>

            <div className="mt-6 space-y-3">
              {dashboardHighlights.map((item, index) => (
                <div className="flex items-start gap-3 rounded-2xl border border-black/6 bg-[#F8F9FA] px-4 py-4" data-testid={`landing-dashboard-point-${index}`} key={item}>
                  <CheckCircle size={20} weight="fill" className="mt-0.5 text-[#FF6B35]" />
                  <p className="text-sm leading-6 text-[#303030]">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="dashboard-preview-grid" data-testid="landing-dashboard-preview-grid">
            <div className="preview-panel tall">
              <p className="preview-label">Calories du jour</p>
              <p className="preview-value">1 845</p>
              <div className="preview-progress"><span style={{ width: "78%" }} /></div>
              <p className="preview-meta">Encore 520 kcal pour atteindre ton objectif.</p>
            </div>
            <div className="preview-panel">
              <p className="preview-label">Protéines</p>
              <p className="preview-value small">132 g</p>
              <p className="preview-meta">Objectif : 150 g</p>
            </div>
            <div className="preview-panel dark">
              <p className="preview-label">Séance du jour</p>
              <p className="preview-value small">Push · 52 min</p>
              <p className="preview-meta">430 kcal brûlées</p>
            </div>
            <div className="preview-panel wide">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="preview-label">Poids</p>
                  <p className="preview-value small">72,4 kg</p>
                </div>
                <div className="mini-chart" aria-hidden="true">
                  <span /><span /><span /><span /><span />
                </div>
              </div>
              <p className="preview-meta">Tendance stable et régulière cette semaine.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 pb-16 pt-8 md:px-8 lg:px-10 lg:pb-24" data-testid="landing-final-cta-section">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-black/10 bg-[#0A0A0A] px-6 py-10 text-white lg:px-10 lg:py-12">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="landing-section-kicker text-white/60" data-testid="landing-final-kicker">myfitbody.fr</p>
              <h2 className="landing-section-title text-white" data-testid="landing-final-title">
                Prêt à prendre le contrôle sur tes repas, tes séances et ton suivi ?
              </h2>
              <p className="mt-4 text-base leading-7 text-white/70" data-testid="landing-final-description">
                Commence par le tableau de bord si tu veux aller droit au but, ou personnalise ton profil si tu veux une base encore plus précise.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link className="landing-primary-button" data-testid="landing-final-dashboard-button" to="/dashboard">
                Ouvrir le tableau de bord
              </Link>
              <Link className="landing-secondary-button dark" data-testid="landing-final-onboarding-button" to="/onboarding">
                Personnaliser mon profil
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}